import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import TimelineIcon from '@mui/icons-material/Timeline';
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
  borderBottom: '1px solid #e2e8f0',
  marginBottom: 0,
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: 64,
  paddingLeft: 0,
  paddingRight: 0,
  [theme.breakpoints.up('sm')]: {
    minHeight: 72,
  },
}));

const LogoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const LogoIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: theme.spacing(1),
  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '1.25rem',
  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
}));

const BrandTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 700,
  color: '#1a202c',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const BrandSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.8125rem',
  color: '#64748b',
  fontWeight: 500,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const NavSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const NavButton = styled(Button)<{ active?: boolean }>(({ theme, active }) => ({
  color: active ? '#3b82f6' : '#64748b',
  backgroundColor: active ? '#eff6ff' : 'transparent',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1, 2),
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: active ? '#dbeafe' : '#f8fafc',
    color: active ? '#3b82f6' : '#475569',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(0.75),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 1.5),
    '& .MuiButton-startIcon': {
      marginRight: 0,
      marginLeft: 0,
    },
    '& .button-text': {
      display: 'none',
    },
  },
}));

interface HeaderProps {
  showGanttButton?: boolean;
}

const Header: React.FC<HeaderProps> = ({ showGanttButton = true }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isGantt = location.pathname === '/gantt';

  return (
    <StyledAppBar position="sticky" elevation={0}>
      <Container maxWidth="xl">
        <StyledToolbar>
          <LogoSection sx={{ flexGrow: 1 }}>
            <LogoIcon>
              <DashboardIcon />
            </LogoIcon>
            <Box>
              <BrandTitle>
                TeamSync PMS
              </BrandTitle>
              <BrandSubtitle>
                專案管理系統
              </BrandSubtitle>
            </Box>
          </LogoSection>
          
          <NavSection>
            <Button
              component={Link}
              to="/"
              startIcon={<HomeIcon fontSize="small" />}
              sx={{
                color: isHome ? '#3b82f6' : '#64748b',
                backgroundColor: isHome ? '#eff6ff' : 'transparent',
                borderRadius: 1,
                padding: '8px 16px',
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: isHome ? '#dbeafe' : '#f8fafc',
                  color: isHome ? '#3b82f6' : '#475569',
                },
                '& .MuiButton-startIcon': {
                  marginRight: '6px',
                },
                '@media (max-width: 600px)': {
                  padding: '6px 12px',
                  '& .MuiButton-startIcon': {
                    marginRight: 0,
                    marginLeft: 0,
                  },
                  '& .button-text': {
                    display: 'none',
                  },
                },
              }}
            >
              <span className="button-text">任務總覽</span>
            </Button>
            {showGanttButton && (
              <Button
                component={Link}
                to="/gantt"
                startIcon={<TimelineIcon fontSize="small" />}
                sx={{
                  color: isGantt ? '#3b82f6' : '#64748b',
                  backgroundColor: isGantt ? '#eff6ff' : 'transparent',
                  borderRadius: 1,
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: isGantt ? '#dbeafe' : '#f8fafc',
                    color: isGantt ? '#3b82f6' : '#475569',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: '6px',
                  },
                  '@media (max-width: 600px)': {
                    padding: '6px 12px',
                    '& .MuiButton-startIcon': {
                      marginRight: 0,
                      marginLeft: 0,
                    },
                    '& .button-text': {
                      display: 'none',
                    },
                  },
                }}
              >
                <span className="button-text">甘特圖</span>
              </Button>
            )}
          </NavSection>
        </StyledToolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Header; 